import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Download } from 'lucide-react'
import { api, type Invoice } from '@/lib/api'
import { cn, formatCurrency, formatRelative } from '@/lib/utils'

const STATUS_CHIP: Record<Invoice['status'], string> = {
  open:      'bg-status-review-bg text-status-review-fg',
  submitted: 'bg-status-draft-bg text-status-draft-fg',
  paid:      'bg-status-approved-bg text-status-approved-fg',
}

function utilisationColor(pct: number): string {
  if (pct >= 95) return '#E24B4A'
  if (pct >= 80) return '#EF9F27'
  return '#1D9E75'
}

export function InvoicingPage() {
  const [tab, setTab] = useState<'all' | Invoice['status']>('all')
  const { data: writers = [] } = useQuery({ queryKey: ['writers'], queryFn: api.writers })
  const { data: invoices = [] } = useQuery({ queryKey: ['invoices'], queryFn: api.invoices })

  const totals = writers.reduce(
    (acc, w) => ({
      spent: acc.spent + w.spent_cents,
      budget: acc.budget + w.monthly_budget_cents,
      pieces: acc.pieces + w.pieces_mtd,
    }),
    { spent: 0, budget: 0, pieces: 0 },
  )
  const openCount = invoices.filter((i) => i.status === 'open').length
  const openAmount = invoices.filter((i) => i.status === 'open').reduce((s, i) => s + i.amount_cents, 0)

  const filtered = tab === 'all' ? invoices : invoices.filter((i) => i.status === tab)

  function exportCsv() {
    const header = ['invoice_id', 'writer', 'period_start', 'period_end', 'pieces', 'amount_usd', 'status', 'submitted_at', 'paid_at']
    const rows = filtered.map((i) => [
      i.id,
      i.writer_name,
      i.period_start,
      i.period_end,
      i.pieces_count,
      (i.amount_cents / 100).toFixed(2),
      i.status,
      i.submitted_at ?? '',
      i.paid_at ?? '',
    ])
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `invoices-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header className="flex justify-between items-start">
        <div>
          <h1 className="text-xl font-medium">Writer invoicing</h1>
          <p className="text-xs text-ink-secondary mt-0.5">
            Month-to-date budget burndown and open invoices. Export to feed your bookkeeper.
          </p>
        </div>
        <button
          onClick={exportCsv}
          className="text-xs border border-border rounded-md px-2.5 py-1.5 hover:bg-surface-muted flex items-center gap-1.5"
        >
          <Download size={13} /> Export CSV
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="MTD spend" value={formatCurrency(totals.spent)} sub={`of ${formatCurrency(totals.budget)} budget`} />
        <Kpi
          label="Budget used"
          value={`${Math.round((totals.spent / Math.max(1, totals.budget)) * 100)}%`}
          sub={`${formatCurrency(totals.budget - totals.spent)} remaining`}
        />
        <Kpi label="Pieces MTD" value={totals.pieces.toString()} sub={`${writers.length} active writers`} />
        <Kpi label="Open invoices" value={openCount.toString()} sub={formatCurrency(openAmount)} tone="warn" />
      </div>

      <section className="border border-border rounded-md bg-surface-card overflow-x-auto">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-medium">Writer roster</h2>
        </div>
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-xs text-ink-tertiary border-b border-border">
              <th className="text-left px-4 py-2 font-normal">Writer</th>
              <th className="text-left px-2 py-2 font-normal">Specialty</th>
              <th className="text-right px-2 py-2 font-normal">Rate</th>
              <th className="text-right px-2 py-2 font-normal">Pieces</th>
              <th className="text-left px-2 py-2 font-normal w-[280px]">MTD spend vs budget</th>
              <th className="text-right px-4 py-2 font-normal">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {writers.map((w) => {
              const pct = Math.min(100, Math.round((w.spent_cents / Math.max(1, w.monthly_budget_cents)) * 100))
              return (
                <tr key={w.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-800 text-[10px] font-medium flex items-center justify-center">
                        {w.avatar_initials}
                      </span>
                      <div>
                        <div className="font-medium">{w.name}</div>
                        <div className="text-[11px] text-ink-tertiary">{w.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-xs text-ink-secondary">{w.specialty ?? '—'}</td>
                  <td className="px-2 py-3 text-right text-xs tabular-nums text-ink-secondary">
                    {formatCurrency(w.rate_cents)}/{w.rate_unit}
                  </td>
                  <td className="px-2 py-3 text-right text-xs tabular-nums">{w.pieces_mtd}</td>
                  <td className="px-2 py-3">
                    <div className="flex justify-between text-[11px] mb-1 text-ink-secondary tabular-nums">
                      <span>{formatCurrency(w.spent_cents)}</span>
                      <span>{formatCurrency(w.monthly_budget_cents)}</span>
                    </div>
                    <div className="h-1.5 bg-surface-muted rounded overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, background: utilisationColor(pct) }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-xs tabular-nums">
                    {formatCurrency(w.monthly_budget_cents - w.spent_cents)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>

      <section className="border border-border rounded-md bg-surface-card overflow-x-auto">
        <div className="flex items-center justify-between border-b border-border px-4">
          <div className="flex overflow-x-auto">
            {(['all', 'open', 'submitted', 'paid'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  'text-xs py-2.5 px-3 border-b-2 -mb-px capitalize',
                  tab === t
                    ? 'border-brand-600 text-ink-primary font-medium'
                    : 'border-transparent text-ink-tertiary hover:text-ink-secondary',
                )}
              >
                {t}
                <span className="text-[10px] text-ink-tertiary ml-1">
                  ({t === 'all' ? invoices.length : invoices.filter((i) => i.status === t).length})
                </span>
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-xs text-ink-tertiary border-b border-border">
              <th className="text-left px-4 py-2 font-normal">Writer</th>
              <th className="text-left px-2 py-2 font-normal">Period</th>
              <th className="text-right px-2 py-2 font-normal">Pieces</th>
              <th className="text-right px-2 py-2 font-normal">Amount</th>
              <th className="text-left px-2 py-2 font-normal">Status</th>
              <th className="text-left px-4 py-2 font-normal">Last update</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => {
              const last = i.paid_at ?? i.submitted_at ?? i.created_at
              return (
                <tr key={i.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 text-sm font-medium">{i.writer_name}</td>
                  <td className="px-2 py-2.5 text-xs text-ink-secondary">
                    {format(new Date(i.period_start), 'MMM d')} –{' '}
                    {format(new Date(i.period_end), 'MMM d, yyyy')}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums">{i.pieces_count}</td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums font-medium">
                    {formatCurrency(i.amount_cents)}
                  </td>
                  <td className="px-2 py-2.5">
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded', STATUS_CHIP[i.status])}>
                      {i.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-ink-tertiary">{formatRelative(last)}</td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-xs text-ink-tertiary">
                  No invoices in this bucket.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  )
}

function Kpi({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string
  value: string
  sub: string
  tone?: 'default' | 'warn'
}) {
  return (
    <div className="border border-border rounded-md bg-surface-card p-3">
      <div className="text-[11px] text-ink-tertiary uppercase tracking-wider">{label}</div>
      <div
        className={cn(
          'text-xl font-medium mt-1 tabular-nums',
          tone === 'warn' && 'text-status-review-fg',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-ink-secondary mt-0.5">{sub}</div>
    </div>
  )
}
